import { IChannelCard } from '@intigral/ui/components/LiveTV/ChannelCard';
import { BRAZE } from '@libs/analytics-events';
import { useCallback, useContext } from 'react';
import { EVENT, KEYS } from '../contexts/MixpanelConstants';
import { MixpanelContext } from '../contexts/MixpanelContext';
import { getMixpanelItemTypeProperty, getMixpanelLinearProperties } from '../contexts/MixpanelHelper';
import playerService from '../helpers/bitmovinPlayer';
import channelsUtil from '../helpers/channelsUtil';
import h from '../helpers/common';
import useBraze from '../hooks/useBraze';

interface Selected {
    channelNumber?: string | number;
    channelTitle?: string;
    channelName?: string;
    category?: string | string[];
    channelCategory?: string | string[];
    startTime?: number;
    endTime?: number;
    title?: string;
    name?: string;
    id?: string | number;
    listingId?: string | number;
    channel?: {
        channelTitle?: string;
        category?: string | string[];
    };
}

const normalizeLinearProgram = (selected: Selected) => {
    const channel = selected.channel ?? {};
    const category = selected.category ?? selected.channelCategory ?? channel.category;
    const channelTitle = selected.channelTitle ?? selected.channelName ?? channel.channelTitle ?? '';
    const title = selected.title ?? selected.name ?? '';

    return {
        ...selected,
        channelTitle,
        channelName: selected.channelName ?? channelTitle,
        title,
        category,
    };
};

const PROGRAM_WATCHED_DEDUPE_MS = 2000;
let recentProgramWatch: { key: string; time: number } | null = null;

const useEventTracking = () => {
    const { track, setTimeEvent, incrementProperty, setUserProperties } = useContext(MixpanelContext);
    const { logBrazeCustomEvent } = useBraze();

    // Triggered when Live TV starts playing
    const sendLiveTVPlayingEvent = (selected: Selected): void => {
        setTimeEvent(`${h.getScreen()} - ${EVENT.LIVE_TV.WATCHED}`);
        track(`${h.getScreen()} - ${EVENT.LIVE_TV.START_PLAYING}`, getMixpanelItemTypeProperty(selected));
    };

    // Track buffering event during Live TV playback
    const sendLiveTVBufferingEvent = (selected: Selected): void => {
        const eventName = `${h.getScreen()} - ${EVENT.LIVE_TV.BUFFERING}`;
        setTimeEvent(eventName);
        track(eventName, getMixpanelItemTypeProperty(selected));
    };

    // Track user interaction with the Live TV play/pause
    const sendLiveTVPlayPauseEvent = ({ paused }): void => {
        const event = paused ? EVENT.LIVE_TV.PAUSE_CLICK : EVENT.LIVE_TV.PLAY_CLICK;
        track(`${h.getScreen()} - ${event}`, {});
    };

    // Track user seeking action during Live TV playback
    const sendLiveTVSeekedEvent = (): void => {
        const eventName = `${h.getScreen()} - ${EVENT.LIVE_TV.SEEK}`;
        track(eventName, {});
    };

    // Handles cleanup when the user stops watching Live TV:
    const sendDestroyEvent = useCallback(
        async (selected: Selected | null | undefined) => {
            if (!selected) {
                return;
            }

            const programKey = String(selected.id ?? selected.listingId ?? '');
            const now = Date.now();
            if (
                programKey &&
                recentProgramWatch?.key === programKey &&
                now - recentProgramWatch.time < PROGRAM_WATCHED_DEDUPE_MS
            ) {
                return;
            }
            if (programKey) {
                recentProgramWatch = { key: programKey, time: now };
            }

            const program = normalizeLinearProgram(selected);

            // Increment total watch hours
            incrementProperty(KEYS.INCREMENT.TOTAL_WATCH_HOURS);

            // Update user property with current datetime
            setUserProperties({
                [KEYS.PEOPLE.LAST_WATCH]: channelsUtil.getCurrentDateTime(),
            });

            // Track event in Mixpanel
            const mixpanelEventName = `${h.getScreen()} - ${EVENT.LIVE_TV.WATCHED}`;
            track(mixpanelEventName, getMixpanelLinearProperties(program));

            const { channelTitle = '', title = '', category } = program;
            const isLive = channelsUtil.isNow(program);
            const programType = isLive ? 'Live' : 'Rewind';
            const channelCategory = Array.isArray(category) ? category.join(',') : category ?? '';
            const duration = playerService?.player?.getCurrentTime?.() ?? 0;

            logBrazeCustomEvent(BRAZE.EVENTS.PROGRAM_WATCHED, {
                [BRAZE.PROPERTIES.CHANNEL_NAME]: channelTitle,
                [BRAZE.PROPERTIES.PROGRAM_NAME]: title,
                [BRAZE.PROPERTIES.PROGRAM_TYPE]: programType,
                [BRAZE.PROPERTIES.CHANNEL_CATEGORY]: channelCategory,
                [BRAZE.PROPERTIES.DURATION]: duration,
            });
        },
        [incrementProperty, logBrazeCustomEvent, setUserProperties, track]
    );

    // Track the search result click event for Live TV analytics
    const sendChannelSearchSelectEvent = (selected: any, keyword: string): void => {
        const { channelNumber = 'NA', channelTitle = 'NA', category = 'NA' } = selected;
        track(`${h.getScreen()} - ${EVENT.LIVE_TV.SEARCH_RESULTS_CLICK}`, {
            Keyword: keyword,
            'Channel Number': channelNumber,
            'Channel Name': channelTitle,
            'Channel Category': category,
        });
    };

    return {
        sendLiveTVPlayingEvent,
        sendLiveTVBufferingEvent,
        sendLiveTVPlayPauseEvent,
        sendLiveTVSeekedEvent,
        sendDestroyEvent,
        sendChannelSearchSelectEvent,
    };
};

export default useEventTracking;
